import { useState, useEffect, useCallback } from 'react';
import { householdService } from '../services/householdService';

export function useHousehold(user) {
  const [householdId, setHouseholdId] = useState(null);
  const [householdName, setHouseholdName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [householdLoading, setHouseholdLoading] = useState(true);
  const [householdError, setHouseholdError] = useState('');
  const [householdMode, setHouseholdMode] = useState('choose');
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [members, setMembers] = useState([]);

  // Reset when user logs out
  useEffect(() => {
    if (!user) {
      setHouseholdId(null);
      setHouseholdLoading(false);
    }
  }, [user]);

  // Load household on login
  useEffect(() => {
    if (!user) return;
    setHouseholdLoading(true);
    (async () => {
      try {
        const profile = await householdService.getUserProfile(user.uid);
        if (profile && profile.householdId) {
          setHouseholdId(profile.householdId);
          setInviteCode(profile.inviteCode || '');
          const info = await householdService.getHouseholdInfo(profile.householdId);
          setHouseholdName(info?.name || 'My Household');
          if (info?.members) {
            const emails = Object.values(info.members).map((m) => m.email).filter(Boolean);
            setMembers(emails);
          }
        }
      } catch (e) {
        console.error(e);
      }
      setHouseholdLoading(false);
    })();
  }, [user]);

  const resetHousehold = useCallback(() => {
    setHouseholdId(null);
    setHouseholdName('');
    setInviteCode('');
    setMembers([]);
    setHouseholdMode('choose');
  }, []);

  const handleCreateHousehold = useCallback(async () => {
    if (!newHouseholdName.trim()) return;
    setSetupLoading(true);
    setHouseholdError('');
    try {
      const r = await householdService.createHousehold(user.uid, user.email, newHouseholdName.trim());
      setHouseholdId(r.householdId);
      setInviteCode(r.inviteCode);
      setHouseholdName(newHouseholdName.trim());
      setMembers([user.email]);
    } catch (e) {
      setHouseholdError(e.message);
    }
    setSetupLoading(false);
  }, [user, newHouseholdName]);

  const handleJoinHousehold = useCallback(async () => {
    if (!joinCode.trim()) return;
    setSetupLoading(true);
    setHouseholdError('');
    try {
      const r = await householdService.joinHousehold(user.uid, user.email, joinCode.trim());
      setHouseholdId(r.householdId);
      setHouseholdName(r.householdName || 'My Household');
      setInviteCode(joinCode.trim().toUpperCase());
    } catch (e) {
      setHouseholdError(e.message);
    }
    setSetupLoading(false);
  }, [user, joinCode]);

  const handleLeaveHousehold = useCallback(async () => {
    if (!user || !householdId) return;
    await householdService.leaveHousehold(user.uid, householdId);
    resetHousehold();
  }, [user, householdId, resetHousehold]);

  return {
    householdId,
    householdName,
    inviteCode,
    householdLoading,
    householdError,
    householdMode,
    setHouseholdMode,
    newHouseholdName,
    setNewHouseholdName,
    joinCode,
    setJoinCode,
    setupLoading,
    members,
    resetHousehold,
    handleCreateHousehold,
    handleJoinHousehold,
    handleLeaveHousehold,
  };
}
